import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { NavBarComponent } from '../nav-bar/nav-bar.component';
import { ActivatedRoute } from '@angular/router';
import { MappingService } from '../services/mapping.service';
import { FarmManagementService } from '../services/farm-management.service';
import { SyncService } from '../services/sync-service';
import { FieldDashboardDataService } from './field-dashboard-data.service';

@Component({
  selector: 'app-field-dashboard-view',
  templateUrl: './field-dashboard-view.component.html',
  styleUrls: ['./field-dashboard-view.component.css'],
  standalone: false,
  encapsulation: ViewEncapsulation.None
})
export class FieldDashboardViewComponent implements OnInit {
  startDate: Date | null = null;
  endDate: Date | null = null;
  selectedSeason: string = '';
  seasons: string[] = [];

  constructor(
    private nav: NavBarComponent,
    private route: ActivatedRoute,
    private mappingService: MappingService,
    private farmService: FarmManagementService,
    private syncService: SyncService,
    private dataService: FieldDashboardDataService
  ) { }

  ngOnInit(): void {
    this.nav.toggleLoading(false);
    this.initializeSeasons();
    this.initializeDateRange();
    
    this.route.paramMap.subscribe(params => {
      const fieldId = params.get('id');
      this.dataService.updateFieldData({ fieldId });
      this.loadFieldData(fieldId);
    });
    
    this.route.queryParamMap.subscribe(queryParams => {
      const farmId = queryParams.get('farmId');
      const tenantId = queryParams.get('tenantId');
      this.dataService.updateFieldData({ farmId, tenantId });
      if (farmId && tenantId) {
        this.loadFarmData(farmId, tenantId);
      }
    });
  }

  loadFieldData(fieldId: string): void {
    if (fieldId) {
      this.dataService.updateFieldData({ fieldInfoLoading: true });
      this.mappingService.getFieldById(Number(fieldId)).subscribe({
        next: (field: any) => {
          this.dataService.updateFieldData({ fieldName: field.name });
        },
        error: (error) => {
          console.error('Error loading field data:', error);
        }
      });

      const now = new Date();
      const cropYear = now.getMonth() >= 5 ? now.getFullYear() + 1 : now.getFullYear();
      this.syncService.getFieldInfo(fieldId, cropYear).subscribe({
        next: (info) => {
          this.dataService.updateFieldData({ crop: info.crop, cultivar: info.cultivar, fieldInfoLoading: false });
        },
        error: (error) => {
          console.error('Error loading field info:', error);
        }
      });
    }
  }

  loadFarmData(farmId: string, tenantId: string): void {
    this.farmService.getTenantFarms(Number(tenantId)).subscribe({
      next: (farms: any[]) => {
        const farm = farms.find(f => f.id === Number(farmId));
        if (farm) {
          this.dataService.updateFieldData({
            farmName: farm.name || '',
            numberOfFields: farm.numberOfFields || 0,
            totalHectares: farm.size || 0,
            country: farm.country || ''
          });
        }
      },
      error: (error) => {
        console.error('Error loading farm data:', error);
      }
    });
  }

  initializeSeasons(): void {
    const now = new Date();
    const currentYear = now.getMonth() >= 5 ? now.getFullYear() + 1 : now.getFullYear();
    for (let i = 0; i < 5; i++) {
      const year = currentYear - i;
      this.seasons.push(`${year - 1}/${year}`);
    }
    this.selectedSeason = this.seasons[0];
    this.dataService.updateFieldData({ season: this.selectedSeason });
  }

  initializeDateRange(): void {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date();
    lastWeek.setDate(yesterday.getDate() - 7);
    this.startDate = lastWeek;
    this.endDate = yesterday;
    this.dataService.updateFieldData({ startDate: lastWeek, endDate: yesterday });
  }

  clearDates(): void {
    this.startDate = null;
    this.endDate = null;
    this.dataService.updateFieldData({ startDate: null, endDate: null });
  }

  onDateChange(): void {
    if (this.startDate && this.endDate) {
      const cropYear = this.endDate.getFullYear();
      const seasonText = `${cropYear - 1}/${cropYear}`;
      this.dataService.updateFieldData({ startDate: this.startDate, endDate: this.endDate, userChangedDates: true, season: seasonText, fieldInfoLoading: true });
      const fieldId = this.dataService.getFieldData().fieldId;
      if (fieldId) {
        this.syncService.getFieldInfo(fieldId, cropYear).subscribe({
          next: (info) => {
            this.dataService.updateFieldData({ crop: info.crop, cultivar: info.cultivar, fieldInfoLoading: false });
          }
        });
      }
    }
  }

  onSeasonChange(): void {
    const startYear = parseInt(this.selectedSeason.split('/')[0]);
    const cropYear = startYear + 1;
    this.dataService.updateFieldData({ season: this.selectedSeason, userChangedDates: false, fieldInfoLoading: true });

    const fieldId = this.dataService.getFieldData().fieldId;
    if (fieldId) {
      this.syncService.getFieldInfo(fieldId, cropYear).subscribe({
        next: (info) => {
          this.dataService.updateFieldData({ crop: info.crop, cultivar: info.cultivar, fieldInfoLoading: false });
        },
        error: (error) => {
          console.error('Error loading field info:', error);
        }
      });
    }
  }
}
